import { useEffect, useRef } from "react";
import { getAccessToken, onToken } from "./api";

/** Mirrors `apps/api/src/lib/events.ts`'s `AppEvent` union. */
export interface RealtimeEvent {
  type: "price.updated" | "price.batch_imported" | "opportunity.published";
  [key: string]: unknown;
}

/**
 * Subscribes to `GET /stream` (SSE) for as long as the caller is mounted and
 * a session is active, reconnecting on drop or on login/logout. Auth via
 * `?access_token=` since `EventSource` can't set an Authorization header —
 * mirrors the mobile app's `sse_service.dart`.
 */
export function useRealtime(onEvent: (event: RealtimeEvent) => void): void {
  const handlerRef = useRef(onEvent);
  handlerRef.current = onEvent;

  useEffect(() => {
    let es: EventSource | null = null;
    let retryTimer: ReturnType<typeof setTimeout> | null = null;
    let stopped = false;

    function open(token: string) {
      es = new EventSource(`/api/v1/stream?access_token=${encodeURIComponent(token)}`);
      es.addEventListener("message", (e) => {
        try {
          handlerRef.current(JSON.parse((e as MessageEvent).data) as RealtimeEvent);
        } catch {
          // malformed event — ignore, next one will still arrive
        }
      });
      es.onerror = () => {
        es?.close();
        es = null;
        if (!stopped) retryTimer = setTimeout(tryOpen, 5000);
      };
    }

    function tryOpen() {
      const token = getAccessToken();
      if (token && !es) open(token);
    }

    tryOpen();
    const off = onToken((token) => {
      if (!token) {
        es?.close();
        es = null;
        if (retryTimer) clearTimeout(retryTimer);
      } else {
        tryOpen();
      }
    });

    return () => {
      stopped = true;
      off();
      if (retryTimer) clearTimeout(retryTimer);
      es?.close();
    };
  }, []);
}
